/**
 * useInstalledLanguages
 * ---------------------
 * Fetches the list of languages installed on this platform from the API
 * (/api/v1/languages/) and caches the result in module scope so subsequent
 * renders are instant.
 *
 * Returns: array of { code, name, flag, dir, is_default }
 *
 * Adding a new language server-side (e.g. French) will automatically
 * surface it here — no frontend code changes needed.
 */
import { useState, useEffect } from 'react';
import axiosInstance from '@/api/axiosInstance';
import { API_PREFIX } from '@/api/endpoints/api.constants';

export interface InstalledLang {
  code: string;
  name: string;
  flag: string;
  is_default: boolean;
  dir: 'ltr' | 'rtl';
}

// RTL language codes (extend as needed — this is just a rendering hint)
const RTL_CODES = new Set(['ar', 'he', 'fa', 'ur', 'ku', 'ps', 'dv', 'sd']);

function addDir(raw: { code: string; name: string; flag: string; is_default: boolean }): InstalledLang {
  return { ...raw, dir: RTL_CODES.has(raw.code) ? 'rtl' : 'ltr' };
}

// Module-level cache — shared across all component instances
let _cache: InstalledLang[] | null = null;
let _promise: Promise<InstalledLang[]> | null = null;

async function fetchLanguages(): Promise<InstalledLang[]> {
  if (_cache) return _cache;
  if (_promise) return _promise;

  _promise = axiosInstance
    .get<{ code: string; name: string; flag: string; is_default: boolean }[]>(
      `${API_PREFIX}/languages/`
    )
    .then(res => {
      const langs = res.data.map(addDir);
      _cache = langs;
      return langs;
    })
    .catch(() => {
      // Fallback: English + Arabic (guaranteed to exist)
      const fallback: InstalledLang[] = [
        { code: 'en', name: 'English',  flag: '🇬🇧', is_default: true,  dir: 'ltr' },
        { code: 'ar', name: 'العربية', flag: '🇸🇦', is_default: false, dir: 'rtl' },
      ];
      _cache = fallback;
      return fallback;
    });

  return _promise;
}

/** Call this to invalidate the cache (e.g. after adding a language in settings) */
export function invalidateLanguageCache() {
  _cache = null;
  _promise = null;
}

// ── Hook ──────────────────────────────────────────────────────────────────────
export function useInstalledLanguages(): { languages: InstalledLang[]; loading: boolean } {
  const [languages, setLanguages] = useState<InstalledLang[]>(_cache ?? []);
  const [loading, setLoading]     = useState(!_cache);

  useEffect(() => {
    let cancelled = false;
    fetchLanguages().then(langs => {
      if (!cancelled) { setLanguages(langs); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, []);

  return { languages, loading };
}
